import React, { useCallback, useEffect, useMemo, useState } from "react";
import Topbar from "../Topbar";
import ContactDetailPage from "../ContactDetailPage";
import { roomsApi } from "./useRoom";
import { initials } from "./parts";
import { itemsWords, lastOpenedWords, notOpenedTag, LIST_FILTERS, filterRooms } from "./roomModel.mjs";
import { shortDate } from "../record/format.mjs";
import "./rooms.css";

/*
 * SALES › PROSPECT ROOMS (RoomsList board): every room he has opened for a
 * prospect, most recently opened first. A row opens the record beside the list,
 * on its Prospect Room panel (the record opens in a panel, never a navigation).
 * A room shared more than three days ago that nobody has opened says so: that is
 * the nudge.
 */

const STAGE_WORDS = {
  new: "New",
  call_scheduled: "Call scheduled",
  in_progress: "In progress",
  evaluating: "Evaluating",
  proposal_sent: "Pricing sent",
  negotiation: "Agreement + legal",
  agreement: "Agreement + legal",
};

function stageWords(row) {
  if (row.company_status === "client") return "Client";
  if (row.company_status && row.company_status !== "prospect") return row.company_status.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
  return STAGE_WORDS[row.pipeline_stage] || "Prospect";
}

export default function RoomsPage() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null); // { contactId, companyId }
  const now = useMemo(() => new Date(), []);

  const load = useCallback(() => roomsApi.list().then(({ rooms }) => setRows(rooms)).catch((e) => setErr(e.message)), []);
  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = {};
    for (const f of LIST_FILTERS) c[f.key] = rows ? filterRooms(rows, f.key, { now }).length : 0;
    return c;
  }, [rows, now]);
  const shown = rows ? filterRooms(rows, filter, { now }) : [];

  return (
    <>
      <Topbar title="Prospect rooms" sub="Every room you’ve opened for a prospect" />
      <div className="rm rm-page">
        <div className="rm-chips" role="group" aria-label="Filter rooms">
          {LIST_FILTERS.map((f) => (
            <button key={f.key} type="button" className={`rm-chip${filter === f.key ? " is-on" : ""}`} aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
              {f.label} {rows ? counts[f.key] : ""}
            </button>
          ))}
        </div>
        {err && <p className="rm-err">{err}</p>}
        <div className="rm-card" style={{ padding: "4px 16px 8px" }}>
          <div className="rm-tablewrap">
            <table className="rm-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Stage</th>
                  <th>Room</th>
                  <th>Items</th>
                  <th>Last updated</th>
                  <th>Last opened ↓</th>
                  <th>Opens 7d</th>
                  <th>People</th>
                </tr>
              </thead>
              <tbody>
                {rows === null && (
                  <tr>
                    <td colSpan={8} className="rm-sub">Loading…</td>
                  </tr>
                )}
                {rows && shown.length === 0 && (
                  <tr>
                    <td colSpan={8} className="rm-sub">
                      {rows.length ? "No rooms match." : "No rooms yet. Open a lead’s record and use Prospect Room in the right column to create one."}
                    </td>
                  </tr>
                )}
                {shown.map((r) => {
                  const tag = notOpenedTag(r, { now });
                  const isOpen = open && open.companyId === r.company_id;
                  return (
                    <tr
                      key={r.id}
                      className={`is-click${isOpen ? " is-open" : ""}`}
                      tabIndex={0}
                      onClick={() => r.contact_id && setOpen({ contactId: r.contact_id, companyId: r.company_id })}
                      onKeyDown={(e) => e.key === "Enter" && r.contact_id && setOpen({ contactId: r.contact_id, companyId: r.company_id })}
                    >
                      <td>
                        <span className="rm-company">
                          <span className="rm-mark" aria-hidden="true">{initials(r.company_name).slice(0, 1)}</span>
                          <span style={{ display: "flex", flexDirection: "column" }}>
                            {r.company_name}
                            {tag && <span className="rm-pill rm-pill--amber" style={{ alignSelf: "flex-start", marginTop: 2 }}>{tag}</span>}
                          </span>
                        </span>
                      </td>
                      <td><span className="rm-pill">{stageWords(r)}</span></td>
                      <td>
                        <span className={`rm-onoff${r.enabled ? "" : " is-off"}`}><i />{r.enabled ? "On" : "Off"}</span>
                      </td>
                      <td className="rm-sub" style={{ whiteSpace: "nowrap" }}>{itemsWords(r.counts)}</td>
                      <td className="rm-sub">{shortDate(r.content_updated_at, { now })}</td>
                      <td style={{ fontWeight: r.last_opened_at ? 600 : 400 }}>{lastOpenedWords(r.last_opened_at, { now })}</td>
                      <td className="crm-num">{r.opens_7d || "–"}</td>
                      <td className="crm-num">{r.people || "–"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="rm-foot">People are told apart by browser, not by name. Opens from you are not counted.</div>
        </div>
      </div>

      {open && (
        <aside className="crm-peek crm-leads__peek">
          <ContactDetailPage
            key={open.contactId}
            contactId={open.contactId}
            embedded
            focus="room"
            hostLabel="Prospect rooms"
            onClose={() => {
              setOpen(null);
              load();
            }}
            onChange={load}
          />
        </aside>
      )}
    </>
  );
}
